/*
 * @Author: Teemo Ding
 * @Date: 2021-10-26 15:55:37
 * @LastEditTime: 2022-05-16 13:06:00
 * @LastEditors: Teemo Ding
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Logistics/WarehouseLocations/ParcelLocker/setting.tsx
 */

import { useRequest } from 'ahooks';
import { Form, Spin, message } from 'antd';
import { get } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';

import ModalFooter from '@/src/components/Antd/Modal/ModalFooter';
import CommonHeader from '@/src/components/CommonHeader';
import { currencies } from '@/src/request/currency';
import { getCourierList } from '@/src/request/settings/logistics/courieraccount';
import {
  deleteParcelLocker,
  getParcelLockerDetail,
  saveParcelLocker,
} from '@/src/request/settings/logistics/parcelLocker';
import { getWareHouseLocation } from '@/src/request/settings/logistics/wareHouseLocation';
import ModalHoc from 'src/constrains/ModalHOC';
import Courier from '../../Components/Courier';
import RecipientInfoSettings from '../../Components/RecipientInfoSettings';
import { CourierAccountType, SFLocation } from '../../CourierAccount/type';
import { breadcrumbData } from '../../config';
import ParcelLockerName from './ParcelLockerName';

import { ConditionType, UnitType } from '@/src/types/common/common';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import ShippingCondition from '../../Components/ShippingCondition';
import ShippingRate, {
  CalculationMethod,
  CourierCalculationMethod,
} from '../../Components/ShippingRate';

import css from './css.module.less';

interface IParams {
  uuid: string;
  warehouseUuid: string;
}

const ParcelLockerSetting = ({ modalOp }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { setShowModal, setModalTitle, setModalContent } = modalOp;
  const history = useHistory();
  const params = useParams<IParams>();
  //const [selectOptions, setSelectOptions] = useState([]);
  const [form] = Form.useForm();
  const [hasModified, setHasModified] = useState<boolean>(false);
  const { uuid, warehouseUuid } = params;

  // const warehouseListActive = warehouseList.filter((item) => {
  //   return item.active;
  // });

  const { loading: getCourierListLoading, data: courierList = [] } = useRequest(
    () => getCourierList(warehouseUuid),
  );

  const { loading: currenciesListLoading, data: currenciesList = [] } =
    useRequest(() => currencies(), {
      onSuccess: () => {
        if (uuid) {
          _getParcelLockerDetail();
        } else {
          form.setFieldsValue({
            shippingRate: {
              calculationMethod: CalculationMethod.Fixed,
            },
          });
        }
      },
    });

  const {
    loading: locationLoading,
    data: { locationName: warehouseName = '' } = {},
  } = useRequest(() => getWareHouseLocation(warehouseUuid));

  const _breadcrumbData = useMemo(() => {
    const breadcrumbDataCopy = breadcrumbData({ getI18n }).concat({
      url: `/settings/logistics/warehouselocation/setting/${warehouseUuid}`,
      name: warehouseName,
    });
    return breadcrumbDataCopy.concat({
      url: '',
      name: uuid
        ? getI18n('storeLogistics.warehouseLocations.editParcelLocker')
        : getI18n('storeLogistics.warehouseLocations.addParcelLocker'),
    });
  }, [uuid, warehouseName]);

  const initCurrencyData = currenciesList.map((item) => ({
    currency: item.code,
    price: '',
  }));

  const { loading: deleteLoading, run: _deleteParcelLocker } = useRequest(
    (uuid) => deleteParcelLocker(uuid),
    {
      manual: true,
      onSuccess(result) {
        message.success(getI18n('general.deletedsuccessfully'));
        setShowModal(false);
        goBackToList();
      },
    },
  );

  const { loading: saveDeliveryMethodLoading, run: _saveParcelLocker } =
    useRequest(saveParcelLocker, {
      manual: true,
      onSuccess(result) {
        message.success(getI18n('general.savesuccess'));
        goBackToList();
      },
    });

  const { loading: initDeliveryMethodLoading, run: _getParcelLockerDetail } =
    useRequest(() => getParcelLockerDetail(uuid), {
      manual: true,
      onSuccess(result) {
        const { shippingRate } = result;
        const {
          calculationMethod,
          courierCalculationMethod,
          baseLessEqualThan,
          deliveryPrices,
          everyGreaterThan,
          additionalDeliveryPrices,
          percentageCourierFee,
        } = shippingRate;
        form.setFieldsValue({
          name: result.name,
          shippingCondition: result.shippingCondition,
          courierAccount: result.courierAccount.uuid,
          requireLocalNumber: result.requireLocalNumber,
          additionalDeliveryPrices:
            additionalDeliveryPrices || initCurrencyData,
          ...(shippingRate.calculationMethod == CalculationMethod.Fixed && {
            fixedDeliveryPrices: deliveryPrices || initCurrencyData,
          }),
          ...(shippingRate.calculationMethod == CalculationMethod.Advanced && {
            deliveryPrices: deliveryPrices || initCurrencyData,
          }),
          ...(shippingRate.calculationMethod == CalculationMethod.UseCourier &&
            shippingRate.courierCalculationMethod ==
              CourierCalculationMethod.FixAmount && {
              FixAmountDeliveryPrices: deliveryPrices || initCurrencyData,
            }),
          // deliveryPrices: deliveryPrices || initCurrencyData,
          shippingRate: {
            calculationMethod: calculationMethod || CalculationMethod.Fixed,
            courierCalculationMethod:
              courierCalculationMethod || CourierCalculationMethod.None,
            baseLessEqualThan,
            everyGreaterThan,
            percentageCourierFee,
          },
        });
      },
    });

  const goBackToList = () => {
    history.push(
      `/settings/logistics/warehouselocation/setting/${warehouseUuid}`,
    );
  };

  const onValuesChange = () => {
    setHasModified(true);
  };

  const onFinish = (values) => {
    values.courierAccount = values.courierAccount
      ? { uuid: values.courierAccount }
      : null;
    values.warehouse = { uuid: warehouseUuid };
    values.uuid = uuid;
    values.shippingRate.deliveryPrices = values.deliveryPrices;
    values.shippingRate.additionalDeliveryPrices =
      values.additionalDeliveryPrices;
    if (values.shippingRate.calculationMethod == CalculationMethod.Fixed) {
      values.shippingRate.deliveryPrices = values.fixedDeliveryPrices;
    } else if (
      values.shippingRate.calculationMethod == CalculationMethod.Advanced
    ) {
      values.shippingRate.deliveryPrices = values.deliveryPrices;
    } else if (
      values.shippingRate.calculationMethod == CalculationMethod.UseCourier &&
      values.shippingRate.courierCalculationMethod ==
        CourierCalculationMethod.FixAmount
    ) {
      values.shippingRate.deliveryPrices = values.FixAmountDeliveryPrices;
    }
    _saveParcelLocker(values);
  };

  const handleDelete = (uuid) => {
    setModalTitle(
      getI18n('storeLogistics.warehouseLocations.deleteDeliveryMethod'),
    );
    setModalContent(
      <div>
        <div className={css.modalContent}>
          {getI18n(
            'storeLogistics.warehouseLocations.deleteDeliveryMethodContent',
          )}
        </div>
        <ModalFooter
          onCancel={() => {
            setShowModal(false);
          }}
          danger
          okText={getI18n('general.delete')}
          onOk={() => _deleteParcelLocker(uuid)}
        />
      </div>,
    );
    setShowModal(true);
  };

  const courierListFilter = courierList.filter(
    (o) =>
      (o.type =
        CourierAccountType.QSF &&
        get(o, 'qsfAccount.location', '') == SFLocation.HK_MO),
  );
  return (
    <Spin
      spinning={
        currenciesListLoading ||
        locationLoading ||
        saveDeliveryMethodLoading ||
        initDeliveryMethodLoading ||
        deleteLoading
      }>
      <CommonHeader
        breadcrumbData={_breadcrumbData}
        onSaveDisable={!hasModified}
        onSubmit={() => form.submit()}
        onDiscard={goBackToList}
        onGoBackClick={goBackToList}
        onDelete={uuid ? handleDelete : null}
      />
      <div className={css.commonContainer}>
        <Form
          layout={'vertical'}
          form={form}
          className={css.editForm}
          initialValues={{
            shippingCondition: {
              conditionType: ConditionType.Weight,
              unit: UnitType.Kilogram,
            },
          }}
          onFinish={onFinish}
          onValuesChange={onValuesChange}>
          <ParcelLockerName />
          <ShippingCondition form={form} />
          <Courier
            getCourierListLoading={getCourierListLoading}
            courierList={courierListFilter}
            hideSpeed
          />
          <RecipientInfoSettings />
          <ShippingRate
            initCurrencyData={initCurrencyData}
            currenciesListLoading={currenciesListLoading}
          />
        </Form>
      </div>
    </Spin>
  );
};

export default ModalHoc(ParcelLockerSetting);
